// ============================================================
// marketingPage.js — Sitio público (home, nosotros, precios, contacto)
// ------------------------------------------------------------
// - Resalta el link activo de la navbar
// - Toggle del menú móvil
// - Filtros del grid "Lo que puedes crear con Vimo"
// - Toggle Mensual/Anual de los planes de precios
// - Envío de formulario de contacto y newsletter (mock)
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';
import { apiRequest } from './api.js';

const MK_PAGES = ['home', 'nosotros', 'precios', 'contacto', 'guias', 'guia'];

export function initMarketingPage() {
  const page = document.body.dataset.page;
  if (!MK_PAGES.includes(page)) return;

  // 1. Marcar el link activo de la navbar
  const linkMap = {
    home:     'inicio',
    nosotros: 'nosotros',
    precios:  'precios',
    contacto: 'contacto',
    guias:    'guias',
    guia:     'guias',
  };
  const activeKey = linkMap[page];
  qsa(`.mk-nav [data-mk-link="${activeKey}"]`).forEach(a => a.classList.add('is-active'));

  // 2. Toggle del menú móvil
  on(document, 'click', '[data-action="mk-menu-toggle"]', (e, btn) => {
    const topbar = btn.closest('.mk-topbar');
    if (!topbar) return;
    const isOpen = topbar.classList.toggle('is-menu-open');
    btn.setAttribute('aria-expanded', String(isOpen));
  });

  // 2.5. Navbar flotante: añade .is-scrolled al hacer scroll
  //      (solo afecta a páginas con hero pantalla completa; las que
  //      tienen .mk-body-no-hero ya muestran el backdrop por CSS).
  const topbar = qs('#mkTopbar');
  if (topbar && !document.body.classList.contains('mk-body-no-hero')) {
    const onScroll = () => {
      topbar.classList.toggle('is-scrolled', window.scrollY > 20);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // 3. Slider de plantillas + galería + chips del hero
  if (page === 'home') {
    initTemplatesSlider();
    initGallerySlider();
    initIdeaChips();
  }

  // 4. Toggle Mensual/Anual en Precios
  if (page === 'precios') initPricingToggle();

  // 5. Formulario de contacto (mock — el backend hace fetch real)
  if (page === 'contacto') {
    const form = qs('[data-mk-contact-form]');
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      // TODO backend: POST a /api/contact con los campos del form
      toast({
        message: 'Mensaje enviado. Te respondemos en menos de 24 h.',
        type: 'success',
      });
      form.reset();
    });
  }

  // 6. Newsletter del footer (conexión real)
  on(document, 'submit', '[data-mk-newsletter]', async (e) => {
    e.preventDefault();
    const input = e.target.querySelector('input[type="email"]');
    const email = input?.value?.trim();
    if (!email) return;
    
    try {
      const res = await apiRequest('/newsletter', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      toast({ message: res.message || 'Suscrito correctamente', type: 'success' });
      input.value = '';
    } catch (err) {
      toast({ message: err.message || 'Error al suscribirte', type: 'error' });
    }
  });
}

// ── Slider de showcase (Home) ─────────────────────────────────
// Slider con overflow visible, autoplay lento (7 s), dots,
// pausa en hover/focus y soporte de arrastre (touch + ratón).
function initGallerySlider() {
  const viewport = qs('#mkGalleryViewport');
  const track    = qs('#mkGalleryTrack');
  const dotsWrap = qs('#mkGalleryDots');
  if (!viewport || !track || !dotsWrap) return;

  const items = qsa('.mk-gallery-item', track);
  if (!items.length) return;

  const AUTOPLAY_MS = 7000;
  let currentIndex = 0;
  let timer = null;
  let paused = false;

  // Construye los dots (uno por vídeo)
  dotsWrap.innerHTML = items
    .map((_, i) => `<button class="mk-gallery-dot${i === 0 ? ' is-active' : ''}" type="button" data-mk-dot="${i}" aria-label="Mostrar vídeo ${i + 1}"></button>`)
    .join('');

  function update() {
    const item = items[currentIndex];
    if (!item) return;

    const viewportW = viewport.clientWidth;
    const itemLeft  = item.offsetLeft;
    const itemW     = item.offsetWidth;
    const trackW    = track.scrollWidth;

    // Centra el ítem activo dentro del viewport (los vecinos asoman).
    let dx = -itemLeft + (viewportW - itemW) / 2;

    // Sin overscroll: no más allá del borde izquierdo ni del derecho.
    const minDx = viewportW - trackW;       // (negativo)
    dx = Math.max(minDx, Math.min(0, dx));

    track.style.transform = `translateX(${dx}px)`;

    // Solo marcamos el dot activo (los ítems no se resaltan: todos iguales)
    qsa('.mk-gallery-dot', dotsWrap).forEach((d, i) => {
      d.classList.toggle('is-active', i === currentIndex);
    });
  }

  function next() { goTo(currentIndex + 1); }

  function goTo(index) {
    currentIndex = ((index % items.length) + items.length) % items.length;
    update();
  }

  function start() {
    stop();
    if (paused) return;
    timer = setInterval(next, AUTOPLAY_MS);
  }

  function stop() {
    if (timer) { clearInterval(timer); timer = null; }
  }

  function restart() { stop(); start(); }

  // Click en dots
  on(dotsWrap, 'click', '.mk-gallery-dot', (e, dot) => {
    goTo(Number(dot.dataset.mkDot));
    restart();
  });

  // Pausa autoplay con hover/focus
  ['mouseenter', 'focusin'].forEach(ev =>
    viewport.addEventListener(ev, () => { paused = true; stop(); }));
  ['mouseleave', 'focusout'].forEach(ev =>
    viewport.addEventListener(ev, () => { paused = false; start(); }));

  // Recalcula al redimensionar (debounced rudimentario)
  let resizeTo = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTo);
    resizeTo = setTimeout(update, 120);
  });

  // Soporte de arrastre (touch + ratón)
  let dragStartX = 0;
  let dragStartIndex = 0;
  let dragging = false;

  const onPointerDown = (e) => {
    dragging = true;
    dragStartX = (e.touches ? e.touches[0].clientX : e.clientX);
    dragStartIndex = currentIndex;
    stop();
    track.style.transition = 'none';
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    const x = (e.touches ? e.touches[0].clientX : e.clientX);
    const dx = x - dragStartX;
    const current = getComputedStyle(track).transform;
    // Aplicamos un offset transitorio
    track.style.transform = `${current === 'none' ? '' : current} translateX(${dx}px)`;
  };
  const onPointerUp = (e) => {
    if (!dragging) return;
    dragging = false;
    track.style.transition = '';
    const x = (e.changedTouches ? e.changedTouches[0].clientX : e.clientX);
    const dx = x - dragStartX;
    const threshold = 60;
    if      (dx <= -threshold) goTo(dragStartIndex + 1);
    else if (dx >=  threshold) goTo(dragStartIndex - 1);
    else                       update();
    if (!paused) start();
  };

  viewport.addEventListener('mousedown', onPointerDown);
  viewport.addEventListener('mousemove', onPointerMove);
  window.addEventListener('mouseup', onPointerUp);
  viewport.addEventListener('touchstart', onPointerDown, { passive: true });
  viewport.addEventListener('touchmove',  onPointerMove, { passive: true });
  viewport.addEventListener('touchend',   onPointerUp);

  // Init
  update();
  start();
}

// ── Chips de ideas del hero (Home) ───────────────────────────
function initIdeaChips() {
  const input = qs('.mk-hero-prompt-input');
  if (!input) return;
  on(document, 'click', '.mk-idea-chip', (e, chip) => {
    input.value = chip.dataset.idea;
    input.focus();
  });
}

// ── Slider de plantillas con card central destacada (Home) ────
function initTemplatesSlider() {
  const viewport = qs('#mkTplViewport');
  const track    = qs('#mkTplTrack');
  const dotsWrap = qs('#mkTplDots');
  if (!viewport || !track) return;

  const allCards = qsa('.mk-tpl-card', track);
  const AUTOPLAY_MS = 5000;
  let currentIndex = 2; // arranca en la 3ª card para tener desborde a ambos lados
  let timer = null;
  let paused = false;

  function visible() {
    return allCards.filter(c => !c.classList.contains('is-hidden'));
  }

  function buildDots() {
    if (!dotsWrap) return;
    dotsWrap.innerHTML = visible()
      .map((_, i) => `<button class="mk-gallery-dot${i === currentIndex ? ' is-active' : ''}" type="button" data-mk-dot="${i}" aria-label="Plantilla ${i + 1}"></button>`)
      .join('');
  }

  function update() {
    const cards = visible();
    if (!cards.length) return;
    currentIndex = Math.min(currentIndex, cards.length - 1);
    const item = cards[currentIndex];

    // Marca la card activa
    allCards.forEach(c => c.classList.remove('is-active'));
    item.classList.add('is-active');

    // Centra la card activa en el viewport
    const vw     = viewport.clientWidth;
    const iLeft  = item.offsetLeft;
    const iW     = item.offsetWidth;
    const trackW = track.scrollWidth;
    let dx = -iLeft + (vw - iW) / 2;
    dx = Math.max(vw - trackW, Math.min(0, dx));
    track.style.transform = `translateX(${dx}px)`;

    // Dots
    if (dotsWrap) {
      qsa('.mk-gallery-dot', dotsWrap).forEach((d, i) => {
        d.classList.toggle('is-active', i === currentIndex);
      });
    }
  }

  function goTo(index) {
    const len = visible().length;
    currentIndex = ((index % len) + len) % len;
    update();
  }

  function next() { goTo(currentIndex + 1); }
  function stop()  { if (timer) { clearInterval(timer); timer = null; } }
  function start() { stop(); if (!paused) timer = setInterval(next, AUTOPLAY_MS); }
  function restart() { stop(); start(); }

  // Tabs → filtran el slider
  on(document, 'click', '.mk-tool-tab', (e, tab) => {
    const tool = tab.dataset.tool;
    qsa('.mk-tool-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    allCards.forEach(c => {
      c.classList.toggle('is-hidden', tool !== 'all' && c.dataset.tool !== tool);
    });
    currentIndex = Math.min(2, visible().length - 1);
    buildDots();
    update();
    restart();
  });

  // Dots
  if (dotsWrap) {
    on(dotsWrap, 'click', '.mk-gallery-dot', (e, dot) => {
      goTo(Number(dot.dataset.mkDot));
      restart();
    });
  }

  // Hover pausa
  ['mouseenter', 'focusin'].forEach(ev =>
    viewport.addEventListener(ev, () => { paused = true; stop(); }));
  ['mouseleave', 'focusout'].forEach(ev =>
    viewport.addEventListener(ev, () => { paused = false; start(); }));

  // Resize
  let resizeTo = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTo);
    resizeTo = setTimeout(update, 120);
  });

  // Arrastre (ratón + touch)
  let dragStartX = 0, dragStartIndex = 0, dragging = false;

  const onDown = (e) => {
    dragging = true;
    dragStartX = e.touches ? e.touches[0].clientX : e.clientX;
    dragStartIndex = currentIndex;
    stop();
    track.style.transition = 'none';
  };
  const onMove = (e) => {
    if (!dragging) return;
    const x  = e.touches ? e.touches[0].clientX : e.clientX;
    const dx = x - dragStartX;
    const cur = getComputedStyle(track).transform;
    track.style.transform = `${cur === 'none' ? '' : cur} translateX(${dx}px)`;
  };
  const onUp = (e) => {
    if (!dragging) return;
    dragging = false;
    track.style.transition = '';
    const x  = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const dx = x - dragStartX;
    if      (dx <= -60) goTo(dragStartIndex + 1);
    else if (dx >=  60) goTo(dragStartIndex - 1);
    else                update();
    if (!paused) start();
  };

  viewport.addEventListener('mousedown',  onDown);
  viewport.addEventListener('mousemove',  onMove);
  window.addEventListener('mouseup',      onUp);
  viewport.addEventListener('touchstart', onDown, { passive: true });
  viewport.addEventListener('touchmove',  onMove, { passive: true });
  viewport.addEventListener('touchend',   onUp);

  // Init
  buildDots();
  update();
  start();
}

// ── Toggle Mensual/Anual de precios ───────────────────────────
function initPricingToggle() {
  const user = JSON.parse(localStorage.getItem('vimo_user') || '{}');
  const region = user.region || 'ES_LATAM';
  
  const currency = '$'; // Precios en dólares USD
  
  const prices = {
    ES_LATAM: { month: '29,90', year: '23,90' },
    BR: { month: '19,90', year: '15,90' }
  };
  
  const userPrices = prices[region] || prices.ES_LATAM;
  
  const amountEl = qs('#mkPlanAmount');
  if (amountEl) {
    amountEl.dataset.priceMonth = userPrices.month;
    amountEl.dataset.priceYear = userPrices.year;
    amountEl.textContent = userPrices.month; // Mensual por defecto
  }
  
  const currencyEl = qs('#mkPlanCurrency');
  if (currencyEl) currencyEl.textContent = currency;
  
  const trialTextEl = qs('#mkPlanTrialText');
  if (trialTextEl) {
    const limit = region === 'BR' ? '0,50 USD' : '0,90 USD';
    trialTextEl.innerHTML = `<i class="bi bi-clock"></i> Incluye Trial gratuito (Límite: ${limit})`;
  }

  on(document, 'click', '.mk-pricing-toggle-btn', (e, btn) => {
    const billing = btn.dataset.billing;   // 'month' | 'year'
    qsa('.mk-pricing-toggle-btn').forEach(b => b.classList.remove('is-active'));
    btn.classList.add('is-active');

    qsa('.mk-plan-amount').forEach(el => {
      const month = el.dataset.priceMonth;
      const year  = el.dataset.priceYear;
      el.textContent = billing === 'year' ? year : month;
    });
    qsa('.mk-plan-period').forEach(el => {
      el.textContent = billing === 'year' ? '/mes (anual)' : '/mes';
    });
  });
}
